// buffer.js -- persisted, owner-bound attempt-ledger buffer (app-global module, one per process).
// Spec: docs/specs/2026-09-29-attempt-ledger.md §6b (claimed-per-entry loss accounting), §10, §11.
// Single job: hold attempt entries until the flush delivers them, and keep each owner's loss count.
//   L1 = evicted by the 50-entry cap, L3 = older than 72 h, L4 = server 400 (reject()),
//   L2 = app killed while storage is unavailable (memory-only; invisible by design).
// A claim with sent = true that is then dropped by L1/L3 is DISCARDED (A1: undercount, never double).
// Never throws into callers on storage failure; platform.js owns localStorage.
import * as Sentry from '../errorReporting';
import { storageGet, storageSet, randomUUID } from '../platform';

const STORAGE_KEY = 'attempt_ledger_v1';
export const MAX_ENTRIES = 50;
export const MAX_AGE_MS = 72 * 3600 * 1000;
const MAX_DURATION_MS = 600000;
const MAX_LOST_COUNT = 1000;

let processId = randomUUID(); // entries begun by an earlier app process are abandoned on load
let state = null;
let memoryOnly = false;
// Ultron P2-1: ids whose POST is in flight RIGHT NOW (memory only, this process). L1 never evicts
// one -- a delivered row must not also be counted as lost.
const inFlight = new Set();

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
// Ultron P2-2: one malformed stored entry must not wedge every future flush; drop it on load.
function isEntry(e) {
  return !!e && typeof e.attempt_id === 'string' && typeof e.owner_user_id === 'string'
    && typeof e.stage === 'string' && typeof e.terminal === 'boolean'
    && isNum(e.started_ms) && isNum(e.updated_ms) && isNum(e.claimed) && e.claimed >= 0
    && typeof e.sent === 'boolean';
}

function load() {
  if (state) return state;
  state = { entries: [], pending: {} };
  if (memoryOnly) return state;
  const raw = storageGet(STORAGE_KEY);
  if (raw) {
    try {
      const p = JSON.parse(raw);
      if (p && Array.isArray(p.entries) && p.pending && typeof p.pending === 'object') {
        const pending = {};
        for (const [k, v] of Object.entries(p.pending)) if (isNum(v) && v > 0) pending[k] = v;
        state = { entries: p.entries.filter(isEntry), pending };
      }
    } catch {
      Sentry.captureMessage('Attempt ledger: corrupt stored buffer discarded', 'warning');
      /* corrupt blob: start empty (best-effort ledger) */
    }
  }
  return state;
}

function persist() {
  if (memoryOnly) return;
  if (!storageSet(STORAGE_KEY, JSON.stringify(state))) memoryOnly = true; // L2 from here on
}

const find = (id) => load().entries.find((e) => e.attempt_id === id) || null;
function addPending(owner, n) { if (n > 0) state.pending[owner] = (state.pending[owner] || 0) + n; }
function removeEntry(entry) { state.entries = state.entries.filter((e) => e !== entry); }

// L1 / L3 drop (§6b.6).
function dropForLoss(entry) {
  addPending(entry.owner_user_id, 1);
  if (entry.claimed > 0 && !entry.sent) addPending(entry.owner_user_id, entry.claimed);
  removeEntry(entry);
}

export function beginAttempt({ ownerUserId, flow, book, appVersion, platform }, nowMs = Date.now()) {
  const s = load();
  const entry = {
    attempt_id: randomUUID(), owner_user_id: ownerUserId, flow, book: book ?? null,
    app_version: appVersion ?? null, platform, stage: 'webview_open', terminal: false,
    outcome: null, reason_code: null, warning_code: null, http_status: null, duration_ms: null,
    client_ts: null, started_ms: nowMs, updated_ms: nowMs, pid: processId, claimed: 0, sent: false,
  };
  s.entries.push(entry);
  while (s.entries.length > MAX_ENTRIES) {
    const victim = s.entries.find((e) => !inFlight.has(e.attempt_id)); // oldest NOT in flight
    if (!victim) break;
    dropForLoss(victim);
  }
  persist();
  return entry.attempt_id;
}

// The flush brackets each POST with these (Ultron P2-1).
export function markInFlight(attemptId) { inFlight.add(attemptId); }
export function clearInFlight(attemptId) { inFlight.delete(attemptId); }

export function advanceStage(attemptId, stage, nowMs = Date.now()) {
  const e = find(attemptId);
  if (!e || e.terminal) return;
  e.stage = stage; e.updated_ms = nowMs;
  persist();
}

export function finishAttempt(attemptId, coded, nowMs = Date.now()) {
  const e = find(attemptId);
  if (!e || e.terminal) return;
  const d = nowMs - e.started_ms;
  Object.assign(e, {
    stage: coded.stage || e.stage, outcome: coded.outcome, reason_code: coded.reason_code ?? null,
    warning_code: coded.warning_code ?? null, http_status: coded.http_status ?? null,
    duration_ms: d >= 0 && d <= MAX_DURATION_MS ? Math.round(d) : null,
    client_ts: new Date(nowMs).toISOString(), terminal: true, updated_ms: nowMs,
  });
  // #1659: the server's sync counts, only when the exit carries them (complete).
  if (Number.isInteger(coded.failed_count)) {
    Object.assign(e, { imported_count: coded.imported_count, skipped_count: coded.skipped_count,
      failed_count: coded.failed_count, untracked_count: coded.untracked_count });
  }
  persist();
}

export function loadForFlush(uid, nowMs = Date.now()) {
  const s = load();
  for (const e of [...s.entries]) {
    if (nowMs - e.updated_ms > MAX_AGE_MS) { dropForLoss(e); continue; } // L3 (any owner)
    if (!e.terminal && e.pid !== processId) {                           // killed mid-attempt
      Object.assign(e, { terminal: true, outcome: 'abandoned', reason_code: 'unknown', warning_code: null,
        http_status: null, duration_ms: null, client_ts: new Date(e.updated_ms).toISOString() });
    }
  }
  persist();
  return s.entries.filter((e) => e.owner_user_id === uid && e.terminal).map((e) => ({ ...e }));
}

// §6b.1: before the FIRST send, atomically move the owner's pending onto the entry. Never recomputed.
export function claimLoss(attemptId) {
  const e = find(attemptId);
  if (!e) return 0;
  if (!e.sent && e.claimed === 0) {
    const n = state.pending[e.owner_user_id] || 0;
    if (n > 0) { e.claimed = n; delete state.pending[e.owner_user_id]; persist(); }
  }
  return e.claimed;
}

// Pre-send transition: "delivery attempted". Persisted BEFORE the request is invoked.
export function markSent(attemptId) {
  const e = find(attemptId);
  if (!e || e.sent) return;
  e.sent = true;
  persist();
}

// 204 (incl. duplicate): remove; pending untouched (§6b.3).
export function settle(attemptId) {
  const e = find(attemptId);
  if (!e) return;
  removeEntry(e); persist();
}

// 400 (L4): claim back to pending, +1, drop (§6b.4).
export function reject(attemptId) {
  const e = find(attemptId);
  if (!e) return;
  addPending(e.owner_user_id, e.claimed + 1);
  removeEntry(e); persist();
}

export function pendingFor(uid) { return load().pending[uid] || 0; }

// The wire body: coded fields only. owner/pid/claim/sent never leave the device.
export function toRequestBody(e) {
  const lost = Math.min(e.claimed || 0, MAX_LOST_COUNT);
  return {
    attempt_id: e.attempt_id, flow: e.flow, book: e.book, stage: e.stage, outcome: e.outcome,
    reason_code: e.reason_code, warning_code: e.warning_code, http_status: e.http_status,
    duration_ms: e.duration_ms, app_version: e.app_version, platform: e.platform,
    client_ts: e.client_ts, lost_count: lost > 0 ? lost : null,
    // #1659: present only when the entry has them (the server takes all four or none).
    ...(Number.isInteger(e.failed_count) ? { imported_count: e.imported_count, skipped_count: e.skipped_count,
      failed_count: e.failed_count, untracked_count: e.untracked_count } : {}),
  };
}

export function __resetForTests() { state = null; memoryOnly = false; processId = randomUUID(); inFlight.clear(); }
export function __simulateRestart() { state = null; processId = randomUUID(); inFlight.clear(); }
